import React, { useEffect, useState } from 'react';
import {
    PencilSquareIcon, PlusIcon, XMarkIcon,
    CheckCircleIcon, TrashIcon, CakeIcon,
} from '@heroicons/react/24/outline';
import { useCakeSizes } from '../../hooks/useOrders';
import type { CakeSize, CakeSizePayload } from '../../types';

// ── Types ──────────────────────────────────────────────────────────────────────

type SizeForm = {
    label: string;
    serves: string;
    base_price: string;
    sort_order: string;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const emptyForm = (): SizeForm => ({ label: '', serves: '', base_price: '', sort_order: '' });

const FIELD = 'w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ee2b74]';

// ── Component ─────────────────────────────────────────────────────────────────

export default function CakeSizesTab() {
    const { sizes, loading, fetchSizes, createSize, updateSize, toggleActive, deleteSize } = useCakeSizes();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSize, setEditingSize] = useState<CakeSize | null>(null);
    const [form,        setForm]        = useState<SizeForm>(emptyForm());
    const [saving,      setSaving]      = useState(false);
    const [success,     setSuccess]     = useState('');
    const [togglingId,  setTogglingId]  = useState<string | null>(null);
    const [deletingId,  setDeletingId]  = useState<string | null>(null);

    useEffect(() => { void fetchSizes(); }, [fetchSizes]);

    const openCreate = () => {
        setEditingSize(null);
        setForm(emptyForm());
        setSuccess('');
        setIsModalOpen(true);
    };

    const openEdit = (size: CakeSize) => {
        setEditingSize(size);
        setForm({
            label:      size.label,
            serves:     size.serves,
            base_price: (size.base_price / 100).toFixed(2),
            sort_order: String(size.sort_order),
        });
        setSuccess('');
        setIsModalOpen(true);
    };

    const closeModal = () => { setIsModalOpen(false); setSuccess(''); };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload: CakeSizePayload = {
                label:      form.label.trim(),
                serves:     form.serves.trim(),
                base_price: Math.round(parseFloat(form.base_price) * 100),
                sort_order: parseInt(form.sort_order) || 0,
            };
            const ok = editingSize
                ? await updateSize(editingSize.id, payload)
                : await createSize(payload);
            if (ok) {
                setSuccess(editingSize ? 'Size updated!' : 'Size created!');
                setTimeout(closeModal, 1500);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (size: CakeSize) => {
        setTogglingId(size.id);
        await toggleActive(size.id, !size.active);
        setTogglingId(null);
    };

    const handleDelete = async (size: CakeSize) => {
        if (!window.confirm(`Delete "${size.label}"? This cannot be undone.`)) return;
        setDeletingId(size.id);
        const ok = await deleteSize(size.id);
        if (!ok) alert('Delete failed.');
        setDeletingId(null);
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <p className="text-sm text-stone-500">
                    {loading ? 'Loading…' : `${sizes.length} size${sizes.length !== 1 ? 's' : ''}`}
                </p>
                <button onClick={openCreate}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] text-white text-sm font-medium transition-colors">
                    <PlusIcon className="h-4 w-4" />
                    Add Size
                </button>
            </div>

            {loading ? (
                <div className="animate-pulse grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="h-36 rounded-xl bg-stone-100" />
                    ))}
                </div>
            ) : sizes.length === 0 ? (
                <div className="py-12 text-center text-sm text-stone-400">
                    No sizes yet. Add your first cake size.
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sizes.map(size => (
                        <div key={size.id}
                             className={`rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden transition-opacity ${!size.active ? 'opacity-50' : ''}`}>
                            <div className="h-1 w-full bg-[#ee2b74]" />
                            <div className="p-5">
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-xl bg-pink-50 flex items-center justify-center">
                                            <CakeIcon className="h-5 w-5 text-[#ee2b74]" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-stone-900">{size.label}</h4>
                                            <p className="text-xs text-stone-400">{size.serves}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => openEdit(size)}
                                                className="p-1.5 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors">
                                            <PencilSquareIcon className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => void handleDelete(size)}
                                                disabled={deletingId === size.id}
                                                className="p-1.5 rounded-lg text-stone-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50">
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                                <div className="pt-3 border-t border-stone-100 flex items-center justify-between">
                                    <span className="text-2xl font-bold text-stone-900">
                                        R{(size.base_price / 100).toFixed(2)}
                                    </span>
                                    <button onClick={() => void handleToggle(size)}
                                            disabled={togglingId === size.id}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${size.active ? 'bg-[#ee2b74]' : 'bg-stone-200'}`}>
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${size.active ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                                <p className="text-xs text-stone-400 mt-2">Sort order: {size.sort_order}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-[1px]">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-stone-200 overflow-hidden">
                        <div className="h-1 w-full bg-[#ee2b74]" />
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-stone-900">
                                        {editingSize ? 'Edit Size' : 'Add Cake Size'}
                                    </h3>
                                    <p className="text-xs text-stone-500 mt-0.5">
                                        {editingSize ? 'Update size details' : 'Create a new cake size'}
                                    </p>
                                </div>
                                <button onClick={closeModal}
                                        className="rounded-lg p-1 text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors">
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>

                            {success && (
                                <div className="mb-4 flex items-center gap-2 p-3 rounded-lg bg-emerald-50 text-emerald-700 text-sm">
                                    <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
                                    {success}
                                </div>
                            )}

                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Size Label</label>
                                    <input type="text" required value={form.label}
                                           onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
                                           placeholder="e.g. 6 inch"
                                           className={FIELD} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Serves</label>
                                    <input type="text" required value={form.serves}
                                           onChange={e => setForm(p => ({ ...p, serves: e.target.value }))}
                                           placeholder="e.g. Serves 8–10"
                                           className={FIELD} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Base Price (R)</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">R</span>
                                        <input type="number" step="0.01" min="0" required value={form.base_price}
                                               onChange={e => setForm(p => ({ ...p, base_price: e.target.value }))}
                                               placeholder="0.00"
                                               className={`${FIELD} pl-8`} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Sort Order</label>
                                    <input type="number" min="0" value={form.sort_order}
                                           onChange={e => setForm(p => ({ ...p, sort_order: e.target.value }))}
                                           placeholder="0"
                                           className={FIELD} />
                                    <p className="text-xs text-stone-400 mt-1">Lower number = shown first in the app</p>
                                </div>
                                <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
                                    <button type="button" onClick={closeModal} disabled={saving}
                                            className="px-4 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 disabled:opacity-50 transition-colors">
                                        Cancel
                                    </button>
                                    <button type="submit" disabled={saving || !!success}
                                            className="px-4 py-2 rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] text-white text-sm font-medium disabled:opacity-50 transition-colors flex items-center gap-2">
                                        {saving ? 'Saving…'
                                            : success ? <><CheckCircleIcon className="h-4 w-4" /> Saved!</>
                                                : editingSize ? 'Save Changes' : 'Add Size'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}